import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const MalaysiaPage = () => {
  const malaysiaData = {
    name: 'Malaysia',
    region: 'Southeast Asia',
    color: '#7090A0',
    metaTitle: 'Pharma & Nutraceutical Registration in Malaysia | NPRA | ACTD/CTD Dossiers',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Malaysia. NPRA requirements, ICH/ASEAN CTD dossier format, health supplement regulation, NCD-driven demand and key documentation for a high-value, import-dependent market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Malaysia',
      subtitle: 'A high-value, NCD-heavy, import-dependent market with mature NPRA guidelines, ICH/ASEAN CTD dossier requirements, and strong demand for chronic therapies and evidence-based supplements.',
      cta: 'Start Your Malaysia Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~35.1M' },
      { label: 'Income Level', value: 'Upper-middle income' },
      { label: 'Health Expenditure (% of GDP)', value: '~4.7%' },
      { label: 'Pharma Imports (2024)', value: '~USD 2.4B' },
      { label: 'Market Structure', value: '63% imported vs 37% generics' },
      { label: 'Annual Market Growth', value: '~8%' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Regulated, High-Value, Import-Driven',
      stats: [
        'Pharmaceutical market growing at ~8% annually',
        'Medicines by value: ~63% imported vs 37% generics',
        'Pharma imports reached ~USD 2.4 billion in 2024',
        'Public sector: MOH hospitals purchasing via central tenders',
        'Private sector: Private hospitals, chain pharmacies, independent pharmacies',
        'International innovators and strong local generics share market',
        'Quality Indian generics and nutraceuticals well represented',
        'Large, mature supplement market with formal NPRA regulation'
      ],
      opportunities: [
        'High per-capita pharma and supplement spend',
        'Mature supplement market: vitamins, metabolic support, joint health, immunity, probiotics',
        'Women\'s health & fertility products in demand',
        'More formal regulation than many ASEAN countries'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: NCD-Dominant With High Metabolic Risk',
      subtitle: 'NCDs account for ~72% of all premature deaths in Malaysia. The healthcare burden has shifted from infectious diseases to chronic NCDs.',
      categories: [
        {
          name: 'Non-Communicable Diseases (NCDs) — Market Driver',
          items: [
            'NCDs account for ~72% of all premature deaths',
            '1 in 5 adults has diabetes (15.6% prevalence)',
            '1 in 3 has hypertension (29.2% prevalence)',
            'Hypercholesterolaemia: 33.3% of adults',
            'Overweight + obesity: 54.4% of adults',
            'Pharma: Antidiabetics, fixed-dose combos, GLP-1 add-ons, antihypertensives, lipid-lowering, cardiology',
            'Nutra: Metabolic health, cardiometabolic support, weight management, antioxidants, liver support, insulin-sensitivity'
          ]
        },
        {
          name: 'Infectious Diseases — Lower Priority',
          items: [
            'Overall healthcare burden has shifted to NCDs',
            'TB and dengue continue but do not dominate',
            'CVD, diabetes, cancer and kidney disease are major cost drivers',
            'Annual healthcare cost of CVD, diabetes and cancer: >RM 9.65 billion',
            'Portfolio takeaway: Primarily chronic NCD market with long-term therapy demand'
          ]
        },
        {
          name: 'Best-Fit Product Categories',
          items: [
            'Pharma: Chronic cardiometabolic products, specialty generics, quality injectables, oncology (case-by-case)',
            'Nutra: Glycaemic control, cardiometabolic & weight-management, evidence-backed probiotics, omega-3, joint health',
            'Women\'s health: PCOS, fertility-supportive formulations, bone health'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'National Pharmaceutical Regulatory Agency (NPRA) & Drug Control Authority (DCA)',
      description: 'NPRA under Ministry of Health provides scientific evaluation and regulatory recommendations. DCA is the ultimate decision-making body; NPRA serves as its secretariat. Local MAH with appropriate licence required.',
      pharma: {
        timeline: 'Varies based on dossier quality and FRP eligibility',
        requirements: [
          'ACTD (ASEAN CTD) or ICH CTD format required',
          'ACTD with 4 parts: Administrative, Quality, Non-clinical, Clinical',
          'Local MAH with Manufacturer\'s or Import Licence required',
          'API info, manufacturing process, specifications',
          'Stability data (Zone IVb)',
          'Bioequivalence data where required',
          'Administrative screening → technical evaluation (quality, safety, efficacy)',
          'DCA decision based on NPRA recommendation',
          'MAL number issued; periodic renewals and pharmacovigilance required',
          'Facilitated Registration Pathway (FRP) available for reference agency approvals'
        ],
        insight: 'NPRA explicitly requires ICH CTD / ASEAN CTD (ACTD) format. Facilitated pathways available for products approved by stringent reference agencies (EMA, USFDA, etc.).'
      },
      nutra: {
        timeline: 'Depends on classification, ingredient safety, and documentation completeness',
        requirements: [
          'Regulated under NPRA Guideline on Registration of Health Supplements (Appendix 6)',
          'Health supplements include: vitamins, minerals, amino acids, fatty acids, enzymes, probiotics, bioactive substances',
          'Product classification (supplement vs drug vs food) required',
          'Full qualitative/quantitative composition',
          'Safety evidence for certain ingredients or dosage levels',
          'GMP certification for manufacturing (traditional/health supplement GMP)',
          'Clear, compliant labelling - no disease-treatment claims',
          'NPRA-acceptable dosage forms (tablets, capsules, powders, liquids)'
        ],
        insight: 'Malaysia has mature, formal supplement regulation. NPRA guidelines define health supplements and require registration with composition, safety and labelling compliance. Variation guidelines exist for post-approval changes.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Product Registration (NPRA / DCA)',
      steps: [
        'Appoint Local MAH - Malaysia-registered company with appropriate licence (import/manufacturer)',
        'Pre-submission Preparation - Ensure CTD/ACTD dossier and key certificates available',
        'Dossier Format - ACTD (ASEAN CTD) with 4 parts: Administrative, Quality, Non-clinical, Clinical',
        'Core Technical Content - API info, manufacturing, specs, stability (Zone IVb), BE data',
        'NPRA Evaluation - Administrative screening → technical evaluation',
        'DCA Decision & Registration - Approval/rejection based on NPRA recommendation',
        'MAL number issuance - Compliance with conditions, renewals, pharmacovigilance',
        'Facilitated Pathway (Optional) - FRP for products approved by reference agencies'
      ]
    },
    
    nutraPathway: {
      title: 'Health Supplement & Nutraceutical Regulation (NPRA)',
      steps: [
        'Product classification and rationale (supplement vs drug vs food)',
        'Definition scope - Vitamins, minerals, amino acids, fatty acids, enzymes, probiotics, bioactives',
        'Full qualitative/quantitative composition preparation',
        'Safety evidence for certain ingredients or dosage levels',
        'GMP certification for manufacturing site (traditional/health supplement GMP)',
        'NPRA-acceptable dosage forms selection',
        'Labelling artwork preparation (ingredients, dosage, warnings, no therapeutic claims)',
        'Raw material & finished product COAs',
        'NPRA evaluation per Appendix 6 guidelines',
        'Registration approval and post-approval variation management'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (ACTD/CTD)': [
        'NPRA/DCA application forms and fees',
        'CTD/ACTD dossier (Parts I-IV)',
        'Manufacturing licence & GMP certificate',
        'API information & DMF or equivalent',
        'Finished product specs & COA',
        'Stability studies (Zone IVb)',
        'Bioequivalence / clinical data where required',
        'Proposed labels, package inserts and SPC',
        'Foreign approval evidence (CoPP, other RA approvals) - useful for FRP'
      ],
      'Health Supplements': [
        'Product classification and rationale',
        'Full formula with quantities of active ingredients',
        'Raw material & finished product COAs',
        'GMP certificate for manufacturer (supplement/traditional medicine GMP)',
        'Safety data for herbal ingredients or novel substances',
        'Labelling artwork complying with NPRA HS expectations',
        'Ingredients, dosage, warnings documentation',
        'No therapeutic claims verification'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Malaysia Should Be in Your ASEAN Strategy',
      reasons: [
        'High per-capita medicines & supplement spend: USD 2.4B pharma imports annually',
        'NCD-heavy population: ~15-20% diabetes, ~30% hypertension, ~54% overweight/obese',
        'Mature, stable regulatory environment (NPRA + DCA) - predictable documentation standards',
        'Regional hub for ASEAN: ACTD format preparation eases adaptation to other ASEAN regulators',
        'Strong recognition of Indian generics - significant import share',
        'Upper-middle income economy with purchasing power',
        '8% annual pharmaceutical market growth',
        'Facilitated registration pathways available'
      ],
      bestPortfolio: [
        'Pharma: Chronic cardiometabolic products, specialty generics, quality injectables, oncology (case-by-case)',
        'Nutra: Glycaemic control, cardiometabolic & weight-management, evidence-backed probiotics, omega-3, joint health, women\'s health (PCOS, fertility, bone health)'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Register in Malaysia',
      services: [
        'Portfolio & Market Fit Assessment - Match products to Malaysia\'s NCD profile',
        'ACTD/CTD Dossier Preparation - Build complete ACTD dossiers per NPRA DRGD',
        'Health Supplement Registration Strategy - Classify per Appendix 6 & NPRA HS guidelines',
        'Label & Claims Compliance - Align with NPRA nutrition and HS rules',
        'Partner Coordination & FRP Utilisation - Work with local MAH for Facilitated pathways',
        'Lifecycle & Variation Support - Post-approval variations, renewals, safety updates'
      ]
    },
    
    faqs: [
      {
        question: 'Which body regulates medicines in Malaysia?',
        answer: 'The National Pharmaceutical Regulatory Agency (NPRA) evaluates applications and serves as secretariat to the Drug Control Authority (DCA), which makes registration decisions.'
      },
      {
        question: 'What dossier format is required?',
        answer: 'NPRA requires dossiers in ICH CTD or ASEAN CTD (ACTD) format, depending on product type. ACTD is standard for generics and many ASEAN products.'
      },
      {
        question: 'Can a foreign company act as MAH?',
        answer: 'No. Only a locally registered company with the appropriate licence (Import or Manufacturer Licence) can act as MAH and submit to NPRA/DCA.'
      },
      {
        question: 'How are health supplements regulated?',
        answer: 'Under NPRA\'s Guideline on Registration of Health Supplements (Appendix 6) and related HS guidance. Supplements must be registered and meet composition, safety and labelling criteria.'
      },
      {
        question: 'Is Malaysia more attractive for acute or chronic therapies?',
        answer: 'Mainly chronic. NCDs like cardiovascular disease, diabetes and cancer dominate, with associated costs exceeding RM 9.65 billion annually.'
      }
    ]
  };

  return <CountryTemplate countryData={malaysiaData} />;
};
